/**
 * STATUS KEY MAPPING SYSTEM
 * =========================
 *
 * The case template system uses two different key formats for identifying statuses:
 *
 * ## BACKEND FORMAT
 * Pattern: `"status"` or `"status-substatus"` (e.g., "open", "open-new", "closed")
 * Delimiter: STATUS_DELIMITER = "-"
 * Based on: Actual status string values from the case_properties.status.values array
 * Used in: API requests/responses, workflow_rules keys in backend format
 *
 * Examples:
 * - "open" - Status without sub-status
 * - "open-new" - Status "open" with sub-status "new"
 * - "closed" - Status without sub-status
 *
 * ## FRONTEND FORMAT
 * Pattern: `"statusId"` or `"statusId#substatusId"` (e.g., "internal_abc123#internal_def456")
 * Delimiter: STATUS_KEY_DELIMITER = "#"
 * Based on: Internal UUIDs generated by generateInternalId()
 * Used in: Form state, React keys, workflow_rules keys in form format
 *
 * Examples:
 * - "internal_abc123" - Status without sub-status
 * - "internal_abc123#internal_def456" - Status with sub-status
 *
 * ## WHY TWO FORMATS?
 *
 * The dual key system exists because:
 *
 * 1. **Stable References**: Backend keys are based on status values which can change
 *    during editing. If a user renames "open" to "pending", all workflow rules
 *    referencing that status would break. Frontend keys use stable UUIDs that
 *    persist across renames.
 *
 * 2. **React Reconciliation**: React needs stable keys to track which items changed.
 *    Using value-based keys causes React to remount components when values change,
 *    losing focus and scroll position.
 *
 * 3. **Circular Reference Prevention**: Workflow rules reference statuses by key.
 *    If keys were based on values, changing a status value would require updating
 *    all rules that reference it, but we can't update them until we know the new
 *    value, creating a chicken-and-egg problem.
 *
 * ## DATA FLOW
 *
 * ```
 * Backend API Response          Form State                    Backend API Request
 * ───────────────────          ───────────────               ───────────────────
 * workflow_rules: {            workflow_rules: {             workflow_rules: {
 *   "open-new": {...}   --->     "uuid1#uuid2": {...}  --->    "open-new": {...}
 * }                            }                             }
 * ```
 *
 * On load: Backend keys are mapped to frontend keys using the status values and
 *          their corresponding internal IDs.
 * On save: Frontend keys are mapped back to backend keys using the reverse lookup.
 */
import { EnumValueForm } from "src/cases/templates/formUtils/types";
import {
  buildBackendStatusKey,
  buildStatusKey,
} from "src/cases/templates/utils";

/**
 * Delimiter used in backend status keys.
 * Separates status from sub-status: "open-new"
 */
export { STATUS_DELIMITER } from "src/cases/templates/utils";

/**
 * Delimiter used in frontend status keys.
 * Separates status ID from sub-status ID: "uuid1#uuid2"
 */
export const STATUS_KEY_DELIMITER = "#";

/**
 * Re-export key building utilities from utils.ts.
 * These are the authoritative implementations.
 */
export { buildBackendStatusKey, buildStatusKey };

/**
 * Bidirectional mappings between backend and frontend status keys.
 */
export type StatusKeyMappings = {
  /** Maps backend keys (e.g., "open-new") to frontend keys (e.g., "uuid1#uuid2") */
  backendToFrontend: Map<string, string>;
  /** Maps frontend keys (e.g., "uuid1#uuid2") to backend keys (e.g., "open-new") */
  frontendToBackend: Map<string, string>;
};

/**
 * Creates bidirectional mappings between backend and frontend status keys.
 *
 * Call this after enriching status values with internal IDs. The mappings
 * are used during transformation to convert between key formats.
 *
 * @param statusValues - Array of status values with _internalId assigned
 * @returns Object with backendToFrontend and frontendToBackend Maps
 *
 * @example
 * const statuses = [
 *   { value: "open", _internalId: "uuid1", sub_values: [{ value: "new", _internalId: "uuid2" }] },
 *   { value: "closed", _internalId: "uuid3" }
 * ];
 * const mappings = createStatusKeyMappings(statuses);
 * // mappings.backendToFrontend.get("open-new") => "uuid1#uuid2"
 * // mappings.frontendToBackend.get("uuid3") => "closed"
 */
export const createStatusKeyMappings = (
  statusValues: EnumValueForm[],
): StatusKeyMappings => {
  const backendToFrontend = new Map<string, string>();
  const frontendToBackend = new Map<string, string>();

  statusValues.forEach((status) => {
    if (!status.sub_values || status.sub_values.length === 0) {
      // Status without sub-statuses
      const frontendKey = buildStatusKey(status._internalId);
      const backendKey = buildBackendStatusKey({ status: status.value });
      backendToFrontend.set(backendKey, frontendKey);
      frontendToBackend.set(frontendKey, backendKey);
    } else {
      // Status with sub-statuses - each combination gets its own key
      status.sub_values.forEach((subStatus) => {
        const frontendKey = buildStatusKey(
          status._internalId,
          subStatus._internalId,
        );
        const backendKey = buildBackendStatusKey({
          status: status.value,
          subStatus: subStatus.value,
        });
        backendToFrontend.set(backendKey, frontendKey);
        frontendToBackend.set(frontendKey, backendKey);
      });
    }
  });

  return { backendToFrontend, frontendToBackend };
};
